// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { IconWrapper } from "@insite/mobius/Icon";
import { Icon, LazyImage } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Truncate from "@stoneage/App/Components/Truncate";
import IconArrowRightCopper from "@stoneage/XDL/Icons/IconArrowRightCopper";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

type Props = {
    title?: string;
    image?: string;
    author?: string;
    publishDate?: string;
    content?: string;
    link?: string;
    themeColor?: string;
    isCarouselItem?: boolean;
};

interface NewsCardStyles {
    cardContainer?: InjectableCss;
    contentWrapper?: InjectableCss;
    title?: TypographyProps;
    dateAndAuthorWrapper?: InjectableCss;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    summaryWrapper?: TypographyProps;
    readMoreLink?: LinkPresentationProps;
}

const getThemedColor = (themeColor: string) => (themeColor === "light" ? "#4d4d4d" : "#cccccc");

const styles: NewsCardStyles = {
    cardContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            width: calc(100% / 3 - 22px);
            margin-right: 22px;
            margin-bottom: 22px;
            box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.2);
            background: ${({ themeColor }: any) => (themeColor === "light" ? "#ffffff" : "#262626")};

            ${({ isCarouselItem }: any) => (isCarouselItem ? "width: 100% !important;" : "")}

            &&&& img {
                object-fit: cover;
            }

            @media (max-width: 1023px) {
                width: calc(100% / 2 - 22px);
            }
            @media (max-width: 650px) {
                width: 100%;
                margin-right: 0;
            }
        `,
    },
    contentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 20px;
            flex-grow: 1;
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            margin-bottom: 8px;
            font-size: 12px;
            line-height: 18px;
            text-transform: uppercase;
        `,
    },
    title: {
        size: "16px",
        lineHeight: "22px",
        color: "common.black",
        css: css`
            margin-bottom: 12px;
            height: 44px;
        `,
    },
    summaryWrapper: {
        css: css`
            flex-grow: 1;
            margin-bottom: 20px;
            color: ${({ themeColor }: any) => getThemedColor(themeColor)};

            & > *,
            & p,
            & span {
                line-height: 22px !important;
                margin: 0;
            }
        `,
    },
    readMoreLink: {
        css: css`
            display: inline;
            ${IconWrapper} {
                margin-left: 6px;
            }
        `,
    },
};

const NewsCard = ({
    title,
    author,
    publishDate,
    content,
    link,
    image,
    isCarouselItem,
    themeColor = "light",
}: Props) => {
    return (
        <StyledWrapper {...styles.cardContainer} {...({ themeColor } as any)} {...({ isCarouselItem } as any)}>
            <LazyImage src={image} width="420" height="200" />
            <StyledWrapper {...styles.contentWrapper}>
                <StyledWrapper {...styles.dateAndAuthorWrapper}>
                    {author && (
                        <Typography
                            {...styles.authorText}
                            data-test-selector="newslist_author"
                            color={getThemedColor(themeColor)}
                        >
                            {translate("By")}&nbsp;{author}
                        </Typography>
                    )}
                    &nbsp;&nbsp;|&nbsp;&nbsp;
                    {publishDate && (
                        <Typography
                            {...styles.publishDateText}
                            data-test-selector="newslist_publishdate"
                            color={getThemedColor(themeColor)}
                        >
                            <LocalizedDateTime
                                dateTime={new Date(publishDate)}
                                options={{ year: "numeric", month: "long", day: "numeric" }}
                            />
                        </Typography>
                    )}
                </StyledWrapper>
                <Typography variant="h2" {...styles.title}>
                    <Link
                        href={link}
                        color={`${themeColor === "light" ? "common.black" : "common.white"}`}
                        data-test-selector="newslist_pagetitle"
                        css={css`
                            span[class^="Truncated"] {
                                color: inherit;
                            }
                        `}
                    >
                        <Truncate lines={2} lineHeight={22}>
                            {title}
                        </Truncate>
                    </Link>
                </Typography>
                {content && (
                    <StyledWrapper
                        {...styles.summaryWrapper}
                        data-test-selector="newslist_content"
                        {...({ themeColor } as any)}
                    >
                        <Truncate lines={4} lineHeight={22}>
                            {parse(content, parserOptions)}
                        </Truncate>
                    </StyledWrapper>
                )}
                <Link
                    href={link}
                    {...styles.readMoreLink}
                    color={`${themeColor === "light" ? "text.main" : "common.white"}`}
                    data-test-selector={`newsList_ReadMore_${title}`}
                >
                    {translate("Read More")}
                    <Icon src={IconArrowRightCopper} size={9} />
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default NewsCard;
